class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.loadingMarkup = this.predictiveSearchResults.innerHTML;
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.closeStickySearchButton = document.querySelector('.header-search-close');
    this.productToShow = this.dataset.productToShow;
    this.searchDetails = this.querySelector('.search_details');
    this.predictiveSearch = this.querySelector('predictive-search')
    this.isOpen = false;
    this.requestSequence = 0;
    this.quickSearchTrending = this.querySelectorAll('.quickSearchTrending .link');

    this.setupEventListeners();
    this.quickSearchTrending.forEach(element => {
      element.addEventListener('click', this.quickSearchTrendingClick.bind(this));
    });
  }
  
  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));
    
    this.input.addEventListener('input', () => {
      // Invalidate immediately, including the time spent waiting for debounce.
      this.close();
      this.removeAttribute('results');
      this.predictiveSearchResults.innerHTML = this.loadingMarkup;
      if (!this.getQuery()) this.showTrendingAndProducts();
    });
    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 500).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocClick.bind(this));
    if (this.closeStickySearchButton != null) {
      this.closeStickySearchButton.addEventListener('click', this.onCloseStickySearchClick.bind(this));
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    
    if (!searchTerm.length) {
      this.close(true);
      return;
    }
    
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();
    
    if (!searchTerm.length) return this.showTrendingAndProducts();

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
        // this.hideTrendingAndProducts();
      };
    })
  }
  
  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      this.showTrendingAndProducts();
    };
    event.preventDefault();
    
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        this.hideTrendingAndProducts();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';
    
    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (!activeElement || activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }
    
  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }
  
  cancelPendingSearch() {
    this.requestSequence += 1;
    this.searchController?.abort();
    this.searchController = null;
    clearTimeout(this.searchTimeout);
    this.removeAttribute('loading');
  }

  isCurrentSearch(sequence, searchTerm) {
    return sequence === this.requestSequence && searchTerm === this.getQuery();
  }

  async getSearchResults(searchTerm) {
    this.cancelPendingSearch();
    const sequence = this.requestSequence;
    const queryKey = searchTerm.toLowerCase();
    const controller = new AbortController();
    this.searchController = controller;
    let timedOut = false;
    this.searchTimeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 10000);
    this.setLiveRegionLoadingState();

    try {
      let resultsMarkup = this.cachedResults[queryKey];
      if (!resultsMarkup) {
        const response = await fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=${this.productToShow}&section_id=predictive-search`, { signal: controller.signal });
        if (!response.ok) throw new Error(`Search HTTP ${response.status}`);
        const text = await response.text();
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section?.querySelector('[data-predictive-search-live-region-count-value]')) throw new Error('Invalid search response');
        resultsMarkup = section.innerHTML;
        if (!this.isCurrentSearch(sequence, searchTerm)) return;
        this.cachedResults[queryKey] = resultsMarkup;
      }
      if (!this.isCurrentSearch(sequence, searchTerm)) return;
      this.renderSearchResults(resultsMarkup);
      await this.updateViewAllLink(searchTerm, sequence, controller.signal);
    } catch (error) {
      if (!this.isCurrentSearch(sequence, searchTerm)) return;
      if (error.name === 'AbortError' && !timedOut) return;
      this.showSearchError(searchTerm);
    } finally {
      if (sequence === this.requestSequence) {
        clearTimeout(this.searchTimeout);
        this.searchController = null;
        this.removeAttribute('loading');
      }
    }
  }

  showSearchError(searchTerm) {
    const message = 'Não foi possível carregar as sugestões. Você pode continuar pela busca completa.';
    const container = document.createElement('div');
    container.className = 'predictive-search__results-list';
    const paragraph = document.createElement('p');
    paragraph.textContent = message;
    const link = document.createElement('a');
    link.className = 'button button-view-all';
    link.href = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last&type=product`;
    link.textContent = `Buscar “${searchTerm}”`;
    container.append(paragraph, link);
    this.predictiveSearchResults.innerHTML = this.loadingMarkup;
    this.predictiveSearchResults.prepend(container);
    this.removeAttribute('results');
    this.setLiveRegionText(message);
    this.hideTrendingAndProducts();
    this.open();
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (!this.statusElement) return;
    clearTimeout(this.statusTimeout);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    this.statusTimeout = setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setAttribute('open', true);
    this.setLiveRegionResults();
    this.open();
    this.hideTrendingAndProducts();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }
  
  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('[class^="header-navigation"]').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }
  
  open() {
    //this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.cancelPendingSearch();
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }

  showTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return 
    this.trendingAndProductsBlock.classList.add('is-show')
    this.trendingAndProductsBlock.classList.remove('hidden');
  }

  hideTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return 

    this.trendingAndProductsBlock.classList.remove('is-show')
    this.trendingAndProductsBlock.classList.add('hidden');
  }

  onDocClick(e) {
    const isInModal = this.contains(e.target), $target = e.target

    if (!isInModal || $target.closest('.header-search-popup-close') || $target.matches('.header-search-popup-close') || $target.closest('.header-search-close') || $target.matches('.header-search-close')) {
      this.close();
      this.hideTrendingAndProducts();
    }
  }

  onCloseStickySearchClick() {
    this.close();
    this.hideTrendingAndProducts();
    document.querySelector('body').classList.remove('sticky-search-open');
    document.querySelector('body').classList.remove('sticky-search-menu-open');
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-open')
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-custom-open')
  }

  async updateViewAllLink(searchTerm, sequence, signal) {
    const qsViewAllLink = this.querySelector('[data-qs-view-all-link]');
    if (!qsViewAllLink || !this.isCurrentSearch(sequence, searchTerm)) return;
    const linkTotal = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last&type=product`;
    qsViewAllLink.href = linkTotal;
    // A failed optional count must not hide otherwise valid suggestions.
    try {
      const count = await this.getTotalResults(linkTotal, signal);
      if (!this.isCurrentSearch(sequence, searchTerm) || !qsViewAllLink.isConnected) return;
      if (count !== null) qsViewAllLink.textContent = qsViewAllLink.textContent.replace('()', `(${count})`);
    } catch (error) {
      // The full-search link remains usable even when its count is unavailable.
    }
  }

  async getTotalResults(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Search count HTTP ${response.status}`);
    const text = await response.text();
    const count = new DOMParser().parseFromString(text, 'text/html').querySelector('[id^="SearchSection"]')?.dataset.searchCount;
    return count != null && /^\d+$/.test(count) ? count : null;
  }

  quickSearchTrendingClick(event) {
    window.location = event.currentTarget.getAttribute('href');
  }
}

customElements.define('predictive-search', PredictiveSearch);
